/* eslint-disable import/named */
import styled, { css, keyframes, } from 'styled-components';
/* eslint-enable import/named */
import { injectTypography } from '../../utils/injectTypography';
import { getEasing } from '../../utils/getEasing';
import { injectSpacing } from '../../api/spacing/injectSpacing';
import { injectOSXFontSmoothing } from '../../utils/injectOSXFontSmoothing';
const animationDuration = 250;
/* ⚠️ This factor is the result of dividing `copy2` by `copy1` font size for
 * decrease and vice versa for increase.
 * This is necessary since `transform: scale()` does not work with units, so the
 * factor needed to be hard coded and must be updated when typography
 * tokens change.
 */
const widthScaleFactorDecrease = 12 / 16;
const widthScaleFactorIncrease = 16 / 12;
const floatKeyframes = ({ theme }, reverse = false) => {
    const { lineHeight, fontSize: fontSizeBig } = theme.responsive.typography.copy1;
    const { fontSize: fontSizeSmall } = theme.responsive.typography.copy2;
    const fontSizeDelta = `(var(${fontSizeBig}) - var(${fontSizeSmall}))`;
    const verticalAlignAdjust = `${fontSizeDelta} / 2`;
    const translateY = `(var(${lineHeight}) + ${verticalAlignAdjust})`;
    const stateSmall = css `
    width: ${100 * widthScaleFactorDecrease}%;

    font-size: var(${fontSizeSmall});
  `;
    const stateBig = css `
    width: 100%;

    font-size: var(${fontSizeBig});

    transform: translateY(calc(${translateY}));
  `;
    if (reverse) {
        return keyframes `
      0% {
        ${stateSmall}
      }
      0.001% {
        width: 100%;

        font-size: var(${fontSizeBig});

        transform: 
          translateY(calc(${verticalAlignAdjust}))
          scale(${widthScaleFactorDecrease});
      }
      100% {
        ${stateBig}
      }
    `;
    }
    return keyframes `
    0% {
      ${stateBig}
    }
    0.001% {
      width: ${100 * widthScaleFactorDecrease}%;

      font-size: var(${fontSizeSmall});

      transform:
        translateY(calc(${translateY} - ${fontSizeDelta}))
        scale(${widthScaleFactorIncrease});
    }
    100% {
      ${stateSmall}
    }
  `;
};
const injectAnimationStyles = (props) => {
    switch (props.effect) {
        case 'scale':
            return css `
        transform: scale(${widthScaleFactorIncrease});
      `;
        case 'float':
            return css `
        animation-name: ${floatKeyframes(props, true)};

        /* Inverse of the above scale factor to retain it's original size */
        ${StyledOptionalText} {
          transform: scale(${widthScaleFactorDecrease}) translateY(-0.1ex);
        }
      `;
        case 'none':
        default:
            return false;
    }
};
export const StyledOptionalText = styled.span `
  display: inline-block;

  transform-origin: bottom left;

  [dir='rtl'] & {
    transform-origin: bottom right;
  }

  transition-timing-function: ${getEasing};
  transition-duration: ${animationDuration}ms;
  transition-property: transform, color;
`;
const getStateColor = ({ theme, ...props }, color) => {
    if (props.disabled)
        return theme.colors.form.secondary;
    if (props.invalid)
        return theme.colors.form.error;
    return theme.colors.form[color];
};
export const injectCommonStyles = (props) => css `
  display: block;
  padding: 0;

  color: ${getStateColor(props, 'primary')};

  transform-origin: top left;

  transition-timing-function: ${getEasing};
  transition-duration: ${animationDuration}ms;

  [dir='rtl'] & {
    transform-origin: top right;
  }

  &[for] {
    cursor: pointer;
  }

  ${injectOSXFontSmoothing(true)}

  /* stylelint-disable no-duplicate-selectors */

  ${StyledOptionalText} {
    color: ${getStateColor(props, 'secondary')};
  }

  ${!props.empty &&
    css `
    color: ${getStateColor(props, 'secondary')};
  `}

  ${props.disabled &&
    css `
    cursor: not-allowed;
  `}

  ${injectTypography(props, 'copy2')}
  ${injectSpacing('margin')(props)}
`;
export const StyledStaticLabel = styled.label `
  ${(props) => injectCommonStyles(props)}
  transition-property: color;
`;
export const StyledAnimatedLabel = styled.label `
  ${(props) => injectCommonStyles(props)}

  width: ${100 * widthScaleFactorDecrease}%;

  line-height: 20px;

  transform-origin: top left;

  [dir='rtl'] & {
    transform-origin: top right;
  }

  transition-property: ${(props) => props.effect === 'scale' && 'transform,'}
    color;

  ${(props) => props.effect === 'float' &&
    css `
      animation: ${floatKeyframes} ${animationDuration}ms ${getEasing} both;
    `}

  ${(props) => props.disabled &&
    css `
      cursor: not-allowed;
    `}

  *:hover > & {
    color: ${(props) => getStateColor(props, 'strong')};
  }

  *:focus-within > & {
    color: ${(props) => getStateColor(props, 'secondary')};
  }

  ${(props) => props.empty &&
    css `
      *:not(:focus-within) & {
        ${injectAnimationStyles(props)}
      }
    `}
`;
//# sourceMappingURL=FormLabel.styles.js.map